import { forwardRef, type ReactNode, type SelectHTMLAttributes, useId } from "react";
import { cx } from "../cx";
import { Icon } from "../Icon";
import styles from "./Select.module.css";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  options: SelectOption[];
  label?: ReactNode;
  error?: string | null;
  placeholder?: string;
  size?: "sm" | "md";
  containerClassName?: string;
}

/** Native <select> keeps platform keyboard handling and mobile pickers; only the closed control is restyled. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, label, error, placeholder, size = "md", required, id, className, containerClassName, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const errorId = `${fieldId}-error`;
  return (
    <div className={cx(styles.field, containerClassName)}>
      {label && (
        <label htmlFor={fieldId} className={styles.label}>
          {label}
          {required && (
            <span className={styles.required} aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </label>
      )}
      <div className={cx(styles.control, styles[size], error && styles.invalid, rest.disabled && styles.disabled)}>
        <select
          ref={ref}
          id={fieldId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cx(styles.select, className)}
          {...rest}
        >
          {placeholder !== undefined && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
        <Icon name="chevron-down" size={16} className={styles.caret} />
      </div>
      {error && (
        <div id={errorId} className={styles.error} role="alert">
          {error}
        </div>
      )}
    </div>
  );
});
